/**
 * PaymentFeePolicyPanel: the switch that decides who pays the payment rail.
 * ─────────────────────────────────────────────────────────────────────────────
 * This is the only control in the dashboard that changes what a CUSTOMER is
 * asked to pay, so it is built to slow the operator down rather than to feel
 * quick: a worked example of the exact line a customer would see, a reason field
 * the audit log keeps, and a confirm step on the one toggle that moves money.
 * Reading the policy is open to any staff member; saving it is refused
 * server-side for anyone who is not a super administrator.
 *
 * The rate card underneath is a supplier cost, not a customer's business, and is
 * only sent to staff.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  apiFetchPaymentRailPolicy,
  apiUpdatePaymentRailPolicy,
  apiUpdatePaymentRailRates,
} from "../../api/index";
import { C, S, ErrorNote, Loading, SectionTitle, fmtPct, fmtUGX } from "./moneyUi";
// Every label here carries the Settings help tooltip (views/Settings/help/paymentsHelp.js).
import SettingHelp from "../../views/Settings/settingsHelp";

const RAIL_ORDER = ["MOMO", "AIRTEL", "FLW", "CARD", "OTHER", "COD"];
const RAIL_NAME = {
  MOMO: "MTN mobile money",
  AIRTEL: "Airtel money",
  FLW: "Flutterwave mobile money",
  CARD: "Card",
  OTHER: "Other",
  COD: "Cash on delivery",
};

const sw = {
  row: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    padding: "14px 0",
    borderBottom: `1px solid ${C.lineSoft}`,
  },
  label: { fontWeight: 700, color: C.ink, fontSize: 14 },
  help: { color: C.muted, fontSize: 12.5, marginTop: 4, maxWidth: 620, lineHeight: 1.5 },
  track: (on, disabled) => ({
    position: "relative",
    width: 46,
    height: 26,
    flex: "0 0 46px",
    borderRadius: 13,
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    background: on ? C.green : "#cbd5e1",
    opacity: disabled ? 0.5 : 1,
    transition: "background .15s",
    padding: 0,
  }),
  knob: (on) => ({
    position: "absolute",
    top: 3,
    left: on ? 23 : 3,
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(15,23,42,.3)",
    transition: "left .15s",
  }),
  num: {
    width: 110,
    padding: "7px 10px",
    borderRadius: 8,
    border: `1px solid #cbd5e1`,
    fontSize: 14,
    textAlign: "right",
  },
  text: {
    width: 220,
    padding: "7px 10px",
    borderRadius: 8,
    border: `1px solid #cbd5e1`,
    fontSize: 14,
  },
  btn: (primary, disabled) => ({
    border: "none",
    borderRadius: 9,
    padding: "9px 18px",
    fontWeight: 700,
    fontSize: 13.5,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
    background: primary ? C.teal : "#eef2f7",
    color: primary ? "#fff" : C.body,
  }),
  warn: {
    background: "#fff7e6",
    border: "1px solid #f0c36d",
    borderRadius: 10,
    padding: "12px 14px",
    color: "#7a5300",
    fontSize: 13,
    lineHeight: 1.55,
    marginBottom: 16,
  },
  example: {
    background: C.wash,
    border: `1px solid ${C.line}`,
    borderRadius: 10,
    padding: "12px 14px",
    fontSize: 13.5,
    color: C.body,
    lineHeight: 1.6,
  },
};

function Toggle({ on, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!on}
      disabled={disabled}
      onClick={() => !disabled && onChange(!on)}
      style={sw.track(!!on, disabled)}
    >
      <span style={sw.knob(!!on)} />
    </button>
  );
}

function SwitchRow({ label, help, value, onChange, disabled }) {
  return (
    <div style={sw.row}>
      <div>
        <div style={sw.label}>{label}</div>
        <div style={sw.help}>{help}</div>
      </div>
      <Toggle on={value} onChange={onChange} disabled={disabled} />
    </div>
  );
}

export default function PaymentFeePolicyPanel({ canEdit = false }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [rateDraft, setRateDraft] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetchPaymentRailPolicy();
      setData(res.data);
      setDraft({ ...res.data.policy });
      setRateDraft(res.data.rates ? JSON.parse(JSON.stringify(res.data.rates)) : null);
    } catch (e) {
      setError(e?.response?.data?.message || "Couldn't load the payment fee policy.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = data && draft && JSON.stringify(draft) !== JSON.stringify(data.policy);
  // The server's shipped default for a switch, quoted in its hint.
  const defaultSwitch = (key) =>
    typeof data?.defaults?.[key] === "boolean" ? ` Default ${data.defaults[key] ? "on" : "off"}.` : "";
  // The rate card's defaults, as one line under the table (staff responses only).
  const rateDefaultsLine = data?.rateDefaults
    ? RAIL_ORDER.filter((r) => r !== "COD" && data.rateDefaults[r])
        .map((r) => `${RAIL_NAME[r] || r} ${Number(data.rateDefaults[r].percent ?? 0)}%`)
        .join(", ")
    : "";
  const turningOn = data && draft && draft.chargeCustomer === true && data.policy.chargeCustomer !== true;

  const set = (key, value) => {
    setSaved(null);
    setConfirming(false);
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const save = async () => {
    if (turningOn && !confirming) {
      setConfirming(true);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await apiUpdatePaymentRailPolicy({ ...draft, reason });
      setData((d) => ({ ...d, policy: res.data.policy }));
      setDraft({ ...res.data.policy });
      setReason("");
      setConfirming(false);
      setSaved("Policy saved.");
      // Re-read so the worked example reflects the policy the server settled on.
      load();
    } catch (e) {
      setError(e?.response?.data?.message || "Couldn't save the policy.");
    } finally {
      setSaving(false);
    }
  };

  const saveRates = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiUpdatePaymentRailRates({ ...rateDraft, reason: reason || "Rate card update" });
      setRateDraft(JSON.parse(JSON.stringify(res.data.rates)));
      setSaved("Rates saved.");
      load();
    } catch (e) {
      setError(e?.response?.data?.message || "Couldn't save the rates.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <div>
          <h4 style={S.title}>
            <i className="fas fa-sliders-h" style={{ color: C.gold }} />
            Payment processing fees
            <SettingHelp topic="railFeePolicy" />
          </h4>
          <div style={S.subtitle}>
            Mobile money and card providers keep a percentage of every payment Lusuku
            collects, goods and delivery included. This policy decides whether Lusuku
            absorbs that cost or recovers it from the customer.
          </div>
        </div>
      </div>

      {loading && <Loading what="the payment fee policy" />}
      {!loading && error && <ErrorNote>{error}</ErrorNote>}

      {!loading && draft && (
        <>
          <div style={sw.warn}>
            <strong>Before you turn the charge on.</strong> Customers see this line on
            their order and receipt. The checkout screens in the apps and on the web do
            not preview it yet, so a customer paying online would only meet the amount at
            the payment step. The wallet does not yet book the fee as money in either, so
            the nightly reconciliation would report it as money the books do not explain.
            Turn it on once both are in place, or knowingly for a short test.
          </div>

          <SwitchRow
            label={<>Charge customers a payment processing fee<SettingHelp topic="railChargeCustomer" /></>}
            help={
              "Off, Lusuku absorbs what the payment provider keeps, which is how every price " +
              "on the platform is set today. On, each new order paid online carries a processing " +
              "fee line and a higher total; orders already placed do not change. Cash on " +
              "delivery is never charged." + defaultSwitch("chargeCustomer")
            }
            value={draft.chargeCustomer}
            onChange={(v) => set("chargeCustomer", v)}
            disabled={!canEdit || saving}
          />
          <SwitchRow
            label={<>Show the fee as a separate line to customers<SettingHelp topic="railShowToCustomer" /></>}
            help={
              "Recorded only. Customer screens print the fee as its own line whenever an " +
              "order carries one, whatever this says, so turning it off would not hide it." +
              defaultSwitch("showToCustomer")
            }
            value={draft.showToCustomer}
            onChange={(v) => set("showToCustomer", v)}
            disabled={!canEdit || saving}
          />
          <SwitchRow
            label={<>Show vendors the processing cost on their sales<SettingHelp topic="railShowToVendor" /></>}
            help={
              "Adds what the providers kept on the vendor's own sales, and who bore it, to " +
              "the vendor's earnings panel. Nothing is deducted from a vendor's payout either way." +
              defaultSwitch("showToVendor")
            }
            value={draft.showToVendor}
            onChange={(v) => set("showToVendor", v)}
            disabled={!canEdit || saving}
          />
          <SwitchRow
            label={<>Gross up the fee to cover the provider's charge on it<SettingHelp topic="railGrossUp" /></>}
            help={
              "The provider takes its percentage of the whole payment, the fee included, so " +
              "the plain rate falls short. On, the fee is the base times the rate plus any flat " +
              "amount, divided by one minus the rate, so what lands covers the order in full." +
              defaultSwitch("grossUp")
            }
            value={draft.grossUp}
            onChange={(v) => set("grossUp", v)}
            disabled={!canEdit || saving}
          />

          <div style={sw.row}>
            <div>
              <div style={sw.label}>Round the fee up to the nearest<SettingHelp topic="railRoundTo" /></div>
              <div style={sw.help}>
                In the order's own currency, and always upwards, so totals are round
                numbers and the fee never undercollects. 1 means no rounding.
                {data?.defaults?.roundTo != null ? ` Default ${Number(data.defaults.roundTo).toLocaleString()}.` : ""}
              </div>
            </div>
            <input
              type="number"
              min="1"
              step="50"
              style={sw.num}
              value={draft.roundTo}
              disabled={!canEdit || saving}
              onChange={(e) => set("roundTo", Math.max(1, Number(e.target.value) || 1))}
            />
          </div>

          <div style={sw.row}>
            <div>
              <div style={sw.label}>Maximum fee per order<SettingHelp topic="railMaxCharge" /></div>
              <div style={sw.help}>
                The most one order is charged, whatever the rate works out to. 0 means
                no maximum; above it Lusuku absorbs the rest.
                {data?.defaults?.maxCharge != null ? ` Default ${Number(data.defaults.maxCharge).toLocaleString()}.` : ""}
              </div>
            </div>
            <input
              type="number"
              min="0"
              step="1000"
              style={sw.num}
              value={draft.maxCharge}
              disabled={!canEdit || saving}
              onChange={(e) => set("maxCharge", Math.max(0, Number(e.target.value) || 0))}
            />
          </div>

          <div style={{ ...sw.row, borderBottom: "none" }}>
            <div>
              <div style={sw.label}>Fee name on orders and receipts<SettingHelp topic="railFeeLabel" /></div>
              <div style={sw.help}>
                Copied onto each new order, so orders already placed keep the name they
                were created with.
                {data?.defaults?.label ? ` Default "${data.defaults.label}".` : ""}
              </div>
            </div>
            <input
              type="text"
              maxLength={60}
              style={sw.text}
              value={draft.label}
              disabled={!canEdit || saving}
              onChange={(e) => set("label", e.target.value)}
            />
          </div>

          <SectionTitle>Worked example</SectionTitle>
          <div style={sw.example}>
            {data.example?.charge > 0 ? (
              <>
                A {fmtUGX(data.example.basket)} basket paid by MTN mobile money is charged{" "}
                <strong>{fmtUGX(data.example.charge)}</strong> as &quot;{draft.label}&quot;, for a
                total of <strong>{fmtUGX(data.example.customerTotal)}</strong>.{" "}
                {data.example.grossedUp
                  ? "The fee is grossed up, so what lands still covers the vendor, the rider and Lusuku's margin."
                  : "That is the plain rate, so Lusuku still bears the provider's charge on the fee itself."}
              </>
            ) : (
              <>
                A {fmtUGX(data.example?.basket || 63800)} basket is charged nothing extra.
                Lusuku absorbs the provider's charge, estimated at{" "}
                {fmtPct(Number(data.rates?.MOMO?.percent ?? data.rates?.MOMO ?? 3), 1)} of
                everything collected on MTN mobile money.
              </>
            )}
          </div>

          {canEdit && (
            <>
              <SectionTitle>Reason for the change<SettingHelp topic="railChangeReason" /></SectionTitle>
              <input
                type="text"
                style={{ ...sw.text, width: "100%", maxWidth: 560 }}
                placeholder="Kept in the audit log, for example: board approved recovering processing fees from 1 December"
                value={reason}
                disabled={saving}
                onChange={(e) => setReason(e.target.value)}
              />

              {confirming && (
                <div style={{ ...sw.warn, marginTop: 14, marginBottom: 0 }}>
                  <strong>This starts charging customers.</strong> Every new order paid
                  online will carry a {draft.label.toLowerCase()} and a higher total.
                  Press Save again to confirm.
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16 }}>
                <button
                  type="button"
                  style={sw.btn(true, saving || !dirty || reason.trim().length < 4)}
                  disabled={saving || !dirty || reason.trim().length < 4}
                  onClick={save}
                >
                  {saving ? "Saving..." : confirming ? "Confirm and save" : "Save policy"}
                </button>
                {dirty && (
                  <button
                    type="button"
                    style={sw.btn(false, saving)}
                    disabled={saving}
                    onClick={() => {
                      setDraft({ ...data.policy });
                      setConfirming(false);
                      setSaved(null);
                    }}
                  >
                    Discard
                  </button>
                )}
                {saved && <span style={{ color: C.green, fontWeight: 600, fontSize: 13 }}>{saved}</span>}
                {reason.trim().length < 4 && dirty && (
                  <span style={S.muted}>A reason of at least four characters is required.</span>
                )}
              </div>
            </>
          )}

          {!canEdit && (
            <div style={S.note}>
              Only a super administrator can change this. You are seeing the live policy.
            </div>
          )}

          {rateDraft && typeof rateDraft.MOMO === "object" && (
            <>
              <SectionTitle>Provider rate card<SettingHelp topic="railRateCard" /></SectionTitle>
              <div style={S.scroll}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Payment method</th>
                      <th style={{ ...S.th, textAlign: "right" }}>Percent<SettingHelp topic="railRatePercent" /></th>
                      <th style={{ ...S.th, textAlign: "right" }}>Flat amount<SettingHelp topic="railRateFlat" /></th>
                      <th style={{ ...S.th, textAlign: "right" }}>Cap<SettingHelp topic="railRateCap" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {RAIL_ORDER.filter((r) => rateDraft[r]).map((rail) => (
                      <tr key={rail}>
                        <td style={{ ...S.td, fontWeight: 600, color: C.body }}>
                          {RAIL_NAME[rail] || rail}
                        </td>
                        {["percent", "flat", "cap"].map((field) => (
                          <td key={field} style={S.tdNum}>
                            <input
                              type="number"
                              min="0"
                              step={field === "percent" ? "0.1" : "50"}
                              style={{ ...sw.num, width: 90 }}
                              value={rateDraft[rail][field] ?? ""}
                              disabled={!canEdit || saving || rail === "COD"}
                              onChange={(e) => {
                                const raw = e.target.value;
                                setSaved(null);
                                setRateDraft((d) => ({
                                  ...d,
                                  [rail]: { ...d[rail], [field]: raw === "" ? null : Number(raw) },
                                }));
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ ...S.muted, marginTop: 8 }}>
                Flutterwave reports its own fee on each card and Airtel payment, so those
                orders record the real figure and their rows here are only a fallback. MTN
                reports no fee, so the cost of every MTN mobile money order is estimated from
                this table. The wallet books MTN's actual cut separately, at the MTN collection
                fee on the Money In tab; keep the MTN row equal to it.
                {rateDefaultsLine ? ` Defaults when nothing is saved here: ${rateDefaultsLine}; no flat amount and no cap.` : ""}
              </div>
              {canEdit && (
                <button
                  type="button"
                  style={{ ...sw.btn(false, saving), marginTop: 14 }}
                  disabled={saving}
                  onClick={saveRates}
                >
                  {saving ? "Saving..." : "Save rates"}
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
